// \src\context\AdminContext.tsx

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

export interface Banner {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  image: string;
}

export interface Brand {
  id: number;
  name: string;
  logo: string;
}

export interface Subcategory {
  id: number;
  name: string;
}

export interface Category {
  id: number;
  name: string;
  icon: string;
  image: string;
  subcategories: Subcategory[];
}

export interface Product {
  id: number;
  name: string;
  code: string;
  category: number;
  subcategoryId: number;
  brand: string;
  price: number;
  showPrice: boolean;
  isPromo: boolean;
  promoLabel: string;
  description: string;
  specs: string[];
  images: string[];
  slug: string;
  active: boolean;
  activeUntil: string;
  promotionUntil: string;
}

export interface Seller {
  id: number;
  name: string;
  role: string;
  phone: string;
  whatsapp: string;
  photo: string;
  available: boolean;
  inactiveUntil: string;
  store: string;
}

export interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  image: string;
  readTime: string;
  source: string;
  link?: string;
}

export interface RssSource {
  id: number;
  name: string;
  url: string;
  enabled: boolean;
}

export interface Store {
  id: number;
  name: string;
  address: string;
  city: string;
  cep: string;
  phone: string;
  whatsapp: string;
  hours: string;
  mapUrl: string;
  lat: number;
  lng: number;
  googleMapsUrl: string;
  wazeUrl: string;
}

export interface Testimonial {
  id: number;
  name: string;
  text: string;
  stars: number;
  photo: string;
}

export interface JobVacancy {
  id: number;
  title: string;
  type: string;
  description: string;
  urgent: boolean;
  active: boolean;
}

export interface RafflePrize {
  id: number;
  title: string;
  description: string;
  image: string;
}

export type RaffleFieldType =
  | "text"
  | "name"
  | "number"
  | "email"
  | "phone"
  | "cpf"
  | "date"
  | "select"
  | "textarea";

export interface RaffleField {
  id: number;
  label: string;
  fieldType: RaffleFieldType;
  required: boolean;
  options: string[];
  showOnTicket: boolean;
  active: boolean;
}

export interface RaffleWinner {
  id: number;
  prizeId: number;
  entryId: number;
  name: string;
  prize: string;
  photo: string;
}

export interface Raffle {
  id: number;
  title: string;
  description: string;
  bannerImage: string;
  question: string;
  regulation: string;
  startDate: string;
  endDate: string;
  active: boolean;
  visibleAfterEnd: boolean;
  fields: RaffleField[];
  prizes: RafflePrize[];
  winners: RaffleWinner[];
}

export interface SiteSettings {
  logoUrl: string;
  siteName: string;
  primaryColor: string;
  accentColor: string;
  contactPhoneCentral: string;
  contactPhoneFilial: string;
  contactWhatsapp: string;
  contactEmail: string;
  contactHoursWeek: string;
  contactHoursSat: string;
  contactHoursSun: string;
  socialFacebook: string;
  socialInstagram: string;
  socialWhatsapp: string;
  socialYoutube: string;
  allProductsBannerMedia: string;
  companyHeaderMedia: string;
  careersHeaderMedia: string;
}

export interface CompanyInfo {
  historyTitle: string;
  historyText: string;
  mission: string;
  vision: string;
  meta: string;
  foundedYear: number;
  construseteActive: boolean;
  construseteLogo: string;
  construseteBadge: string;
  construseteTitle: string;
  construseteText: string;
  construseteUrl: string;
}

export interface CompanyTimelineItem {
  id: number;
  eventYear: number;
  title: string;
  description: string;
  sortOrder: number;
}

export interface OutletPage {
  title: string;
  subtitle: string;
  description: string;
  heroMedia: string;
  introTitle: string;
  introText: string;
  showcaseTitle: string;
  showcaseText: string;
  showcaseMediaOne: string;
  showcaseMediaTwo: string;
  productsTitle: string;
  productsText: string;
  sellersTitle: string;
  sellersText: string;
  contactTitle: string;
  contactText: string;
  active: boolean;
}

export interface AdminData {
  settings: SiteSettings;
  banners: Banner[];
  brands: Brand[];
  categories: Category[];
  products: Product[];
  sellers: Seller[];
  news: NewsItem[];
  rssSources: RssSource[];
  stores: Store[];
  companyInfo: CompanyInfo;
  companyTimeline: CompanyTimelineItem[];
  outletPage: OutletPage;
  outletProducts: number[];
  promotions: number[];
  testimonials: Testimonial[];
  jobVacancies: JobVacancy[];
  raffles: Raffle[];
}

export interface AdminBackup {
  id: number;
  timestamp: number;
  label: string;
  data: AdminData;
  createdAt?: string;
}

interface ApiDataResponse {
  success?: boolean;
  message?: string;
  data?: Partial<AdminData>;
}

interface BackupApiResponse {
  success?: boolean;
  message?: string;
  created?: boolean;
  id?: number | null;
  backups?: Array<{
    id?: number;
    timestamp?: number;
    label?: string;
    data?: Partial<AdminData>;
    createdAt?: string;
  }>;
  backup?: {
    id?: number;
    timestamp?: number;
    label?: string;
    data?: Partial<AdminData>;
    createdAt?: string;
  };
}

const LEGACY_STORAGE_KEY = "mz_admin_data";

const initialData: AdminData = {
  settings: {
    logoUrl: "/images/logo/logo.png",
    siteName: "M&Z Materiais para Construção",
    primaryColor: "#CC0000",
    accentColor: "#111111",
    contactPhoneCentral: "",
    contactPhoneFilial: "",
    contactWhatsapp: "",
    contactEmail: "vendasmez@mezsantafe.com.br",
    contactHoursWeek: "",
    contactHoursSat: "",
    contactHoursSun: "",
    socialFacebook: "",
    socialInstagram: "",
    socialWhatsapp: "",
    socialYoutube: "",
    allProductsBannerMedia: "/images/banners/05.jpg",
    companyHeaderMedia: "/images/empresa/01.jpg",
    careersHeaderMedia: "/images/propostas/01.jpg",
  },
  banners: [],
  brands: [],
  categories: [],
  products: [],
  sellers: [],
  news: [],
  rssSources: [],
  stores: [],
  companyInfo: {
    historyTitle: "",
    historyText: "",
    mission: "",
    vision: "",
    meta: "",
    foundedYear: 2011,
    construseteActive: true,
    construseteLogo: "/images/logo/logoConstruSete.jpeg",
    construseteBadge: "Associativismo que fortalece",
    construseteTitle: "M&Z e ConstruSete",
    construseteText: "A M&Z integra o Grupo ConstruSete (C7), uma associação de lojas de materiais para construção que promove troca de experiências, ações conjuntas e fortalecimento do setor. Essa participação amplia o relacionamento com fornecedores e parceiros e contribui para oferecer cada vez mais qualidade, variedade e boas oportunidades aos nossos clientes.",
    construseteUrl: "https://www.construsete.com.br/",
  },
  companyTimeline: [],
  outletPage: {
    title: "MZ Outlet",
    subtitle: "Economia e oportunidades para sua obra",
    description: "Confira produtos selecionados, novidades e fale diretamente com a equipe da MZ Outlet.",
    heroMedia: "",
    introTitle: "Conheça a MZ Outlet",
    introText: "Um espaço da M&Z dedicado a produtos selecionados e atendimento direto para ajudar você a encontrar boas oportunidades para sua obra.",
    showcaseTitle: "Destaques da MZ Outlet",
    showcaseText: "Use este espaço para apresentar novidades, campanhas, vídeos, GIFs e outras mídias da Outlet.",
    showcaseMediaOne: "",
    showcaseMediaTwo: "",
    productsTitle: "Produtos da MZ Outlet",
    productsText: "Confira os produtos selecionados para aparecer nesta página.",
    sellersTitle: "Fale com a equipe da MZ Outlet",
    sellersText: "Entre em contato diretamente com nossos vendedores pelo WhatsApp.",
    contactTitle: "Visite a MZ Outlet",
    contactText: "Confira endereço, horários e as opções de rota para chegar até a loja.",
    active: true,
  },
  outletProducts: [],
  promotions: [],
  testimonials: [],
  jobVacancies: [],
  raffles: [],
};

function normalizeProduct(
  product: Product,
): Product {
  return {
    ...product,
    code: product.code || "",
    active: product.active !== false,
    activeUntil: product.activeUntil || "",
    promotionUntil: product.promotionUntil || "",
  };
}

function normalizeSeller(
  seller: Seller,
): Seller {
  const inactiveUntil = seller.inactiveUntil || "";

  const today = new Date();
  const todayString = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");

  const shouldReactivate =
    seller.available === false &&
    inactiveUntil !== "" &&
    inactiveUntil <= todayString;

  return {
    ...seller,
    available: shouldReactivate
      ? true
      : seller.available !== false,
    inactiveUntil: shouldReactivate
      ? ""
      : inactiveUntil,
  };
}

function normalizeTestimonial(
  testimonial: Testimonial,
): Testimonial {
  return {
    ...testimonial,
    photo: testimonial.photo || "",
  };
}

function normalizeJobVacancy(
  vacancy: JobVacancy,
): JobVacancy {
  return {
    ...vacancy,
    title: vacancy.title || "",
    type: vacancy.type || "",
    description: vacancy.description || "",
    urgent: vacancy.urgent === true,
    active: vacancy.active !== false,
  };
}

function normalizeRafflePrize(
  prize: RafflePrize,
): RafflePrize {
  return {
    ...prize,
    title: prize.title || "",
    description: prize.description || "",
    image: prize.image || "",
  };
}

function normalizeRaffleField(
  field: RaffleField,
): RaffleField {
  const allowedTypes: RaffleFieldType[] = [
    "text",
    "name",
    "number",
    "email",
    "phone",
    "cpf",
    "date",
    "select",
    "textarea",
  ];

  const fieldType = allowedTypes.includes(field.fieldType)
    ? field.fieldType
    : "text";

  return {
    ...field,
    id: Number(field.id) || 0,
    label: field.label || "",
    fieldType,
    required: field.required === true,
    options: Array.isArray(field.options)
      ? field.options.map(option => String(option).trim()).filter(Boolean)
      : [],
    showOnTicket: field.showOnTicket !== false,
    active: field.active !== false,
  };
}

function normalizeRaffleWinner(
  winner: RaffleWinner,
): RaffleWinner {
  return {
    ...winner,
    prizeId: Number(winner.prizeId) || 0,
    entryId: Number(winner.entryId) || 0,
    name: winner.name || "",
    prize: winner.prize || "",
    photo: winner.photo || "",
  };
}

function normalizeRaffle(
  raffle: Raffle,
): Raffle {
  return {
    ...raffle,
    title: raffle.title || "",
    description: raffle.description || "",
    bannerImage: raffle.bannerImage || "",
    question: raffle.question || "",
    regulation: raffle.regulation || "",
    startDate: raffle.startDate || "",
    endDate: raffle.endDate || "",
    active: raffle.active !== false,
    visibleAfterEnd: raffle.visibleAfterEnd !== false,
    fields: Array.isArray(raffle.fields)
      ? raffle.fields.map(normalizeRaffleField)
      : [],
    prizes: Array.isArray(raffle.prizes)
      ? raffle.prizes.map(normalizeRafflePrize)
      : [],
    winners: Array.isArray(raffle.winners)
      ? raffle.winners.map(normalizeRaffleWinner)
      : [],
  };
}

function normalizeCompanyTimelineItem(
  item: CompanyTimelineItem,
): CompanyTimelineItem {
  return {
    ...item,
    id: Number(item.id) || 0,
    eventYear: Number(item.eventYear) || 0,
    title: item.title || "",
    description: item.description || "",
    sortOrder: Number(item.sortOrder) || 0,
  };
}

function normalizeOutletPage(
  outletPage?: Partial<OutletPage>,
): OutletPage {
  return {
    ...initialData.outletPage,
    ...(outletPage || {}),
    active: outletPage?.active !== false,
  };
}

function normalizeAdminData(
  serverData: Partial<AdminData>,
): AdminData {
  return {
    settings: {
      ...initialData.settings,
      ...(serverData.settings || {}),
    },
    banners: Array.isArray(serverData.banners)
      ? serverData.banners
      : [],
    brands: Array.isArray(serverData.brands)
      ? serverData.brands
      : [],
    categories: Array.isArray(serverData.categories)
      ? serverData.categories
      : [],
    products: Array.isArray(serverData.products)
      ? serverData.products.map(normalizeProduct)
      : [],
    sellers: Array.isArray(serverData.sellers)
      ? serverData.sellers.map(normalizeSeller)
      : [],
    news: Array.isArray(serverData.news)
      ? serverData.news
      : [],
    rssSources: Array.isArray(serverData.rssSources)
      ? serverData.rssSources
      : [],
    stores: Array.isArray(serverData.stores)
      ? serverData.stores
      : [],
    companyInfo: {
      ...initialData.companyInfo,
      ...(serverData.companyInfo || {}),
    },
    companyTimeline: Array.isArray(serverData.companyTimeline)
      ? serverData.companyTimeline.map(
          normalizeCompanyTimelineItem,
        )
      : [],
    outletPage: normalizeOutletPage(serverData.outletPage),
    outletProducts: Array.isArray(serverData.outletProducts)
      ? serverData.outletProducts.map(Number).filter(id => id > 0)
      : [],
    promotions: Array.isArray(serverData.promotions)
      ? serverData.promotions
      : [],
    testimonials: Array.isArray(serverData.testimonials)
      ? serverData.testimonials.map(
          normalizeTestimonial,
        )
      : [],
    jobVacancies: Array.isArray(serverData.jobVacancies)
      ? serverData.jobVacancies.map(
          normalizeJobVacancy,
        )
      : [],
    raffles: Array.isArray(serverData.raffles)
      ? serverData.raffles.map(normalizeRaffle)
      : [],
  };
}

async function parseApiResponse(
  response: Response,
): Promise<ApiDataResponse> {
  const responseText = await response.text();

  if (!responseText.trim()) {
    return {};
  }

  try {
    return JSON.parse(responseText) as ApiDataResponse;
  } catch {
    throw new Error(
      "O servidor retornou uma resposta inválida.",
    );
  }
}

async function loadDataFromServer(): Promise<AdminData> {
  const response = await fetch("/api/data.php", {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
    },
  });

  const result = await parseApiResponse(response);

  if (
    !response.ok ||
    result.success !== true ||
    !result.data
  ) {
    throw new Error(
      result.message ||
        "Não foi possível carregar os dados do site.",
    );
  }

  return normalizeAdminData(result.data);
}

async function saveDataToServer(
  data: AdminData,
): Promise<AdminData> {
  const response = await fetch("/api/data.php", {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      data,
    }),
  });

  const result = await parseApiResponse(response);

  if (
    !response.ok ||
    result.success !== true ||
    !result.data
  ) {
    throw new Error(
      result.message ||
        "Não foi possível salvar os dados do site.",
    );
  }

  return normalizeAdminData(result.data);
}

/*
|--------------------------------------------------------------------------
| BACKUPS
|--------------------------------------------------------------------------
|
| Os backups são persistidos no banco por /api/backups.php.
| Não utilizamos mais localStorage para salvar ou restaurar backups.
|
*/

async function parseBackupApiResponse(
  response: Response,
): Promise<BackupApiResponse> {
  const responseText = await response.text();

  if (!responseText.trim()) {
    return {};
  }

  try {
    return JSON.parse(
      responseText,
    ) as BackupApiResponse;
  } catch {
    throw new Error(
      "O servidor retornou uma resposta inválida para os backups.",
    );
  }
}

function normalizeBackup(
  backup: {
    id?: number;
    timestamp?: number;
    label?: string;
    data?: Partial<AdminData>;
    createdAt?: string;
  },
): AdminBackup | null {
  const id = Number(
    backup.id ?? 0,
  );

  if (
    id <= 0 ||
    !backup.data ||
    typeof backup.data !== "object"
  ) {
    return null;
  }

  return {
    id,
    timestamp: Number(
      backup.timestamp ?? 0,
    ),
    label:
      typeof backup.label === "string"
        ? backup.label
        : "",
    data: normalizeAdminData(
      backup.data,
    ),
    createdAt:
      typeof backup.createdAt === "string"
        ? backup.createdAt
        : undefined,
  };
}

function normalizeBackups(
  backups: BackupApiResponse["backups"],
): AdminBackup[] {
  if (!Array.isArray(backups)) {
    return [];
  }

  return backups
    .map(normalizeBackup)
    .filter(
      (
        backup,
      ): backup is AdminBackup =>
        backup !== null,
    );
}

export async function loadBackups(): Promise<
  AdminBackup[]
> {
  const response = await fetch(
    "/api/backups.php",
    {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    },
  );

  const result =
    await parseBackupApiResponse(
      response,
    );

  if (
    !response.ok ||
    result.success !== true
  ) {
    throw new Error(
      result.message ||
        "Não foi possível carregar os backups.",
    );
  }

  return normalizeBackups(
    result.backups,
  );
}

export async function saveBackup(
  data: AdminData,
  label?: string,
): Promise<AdminBackup[]> {
  const response = await fetch(
    "/api/backups.php",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type":
          "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        action: "create",
        mode: "manual",
        label:
          label ||
          `Backup ${new Date().toLocaleString("pt-BR")}`,
        data,
      }),
    },
  );

  const result =
    await parseBackupApiResponse(
      response,
    );

  if (
    !response.ok ||
    result.success !== true
  ) {
    throw new Error(
      result.message ||
        "Não foi possível salvar o backup.",
    );
  }

  return normalizeBackups(
    result.backups,
  );
}

export async function restoreBackup(
  backup: AdminBackup,
): Promise<AdminData> {
  const response = await fetch(
    "/api/backups.php",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type":
          "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        action: "restore",
        id: backup.id,
      }),
    },
  );

  const result =
    await parseBackupApiResponse(
      response,
    );

  if (
    !response.ok ||
    result.success !== true ||
    !result.backup?.data
  ) {
    throw new Error(
      result.message ||
        "Não foi possível recuperar o backup.",
    );
  }

  const backupData =
    normalizeAdminData(
      result.backup.data,
    );

  return saveDataToServer(
    backupData,
  );
}

export async function autoSaveBackup(
  data: AdminData,
): Promise<AdminBackup[]> {
  const response = await fetch(
    "/api/backups.php",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type":
          "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        action: "create",
        mode: "auto",
        label:
          `Auto ${new Date().toLocaleString("pt-BR")}`,
        data,
      }),
    },
  );

  const result =
    await parseBackupApiResponse(
      response,
    );

  if (
    !response.ok ||
    result.success !== true
  ) {
    throw new Error(
      result.message ||
        "Não foi possível realizar o auto-backup.",
    );
  }

  return normalizeBackups(
    result.backups,
  );
}

interface AdminContextType {
  data: AdminData;
  loading: boolean;
  saving: boolean;
  error: string;
  refreshData: () => Promise<void>;
  updateSettings: (
    settings: Partial<SiteSettings>,
  ) => void;
  setBanners: (banners: Banner[]) => void;
  setBrands: (brands: Brand[]) => void;
  setCategories: (
    categories: Category[],
  ) => void;
  setProducts: (products: Product[]) => void;
  setSellers: (sellers: Seller[]) => void;
  setNews: (news: NewsItem[]) => void;
  setRssSources: (
    sources: RssSource[],
  ) => void;
  setStores: (stores: Store[]) => void;
  setCompanyInfo: (
    company: CompanyInfo,
  ) => void;
  setCompanyTimeline: (
    timeline: CompanyTimelineItem[],
  ) => void;
  setOutletPage: (outletPage: OutletPage) => void;
  setOutletProducts: (productIds: number[]) => void;
  setPromotions: (
    promotions: number[],
  ) => void;
  setTestimonials: (
    testimonials: Testimonial[],
  ) => void;
  setJobVacancies: (
    vacancies: JobVacancy[],
  ) => void;
  setRaffles: (raffles: Raffle[]) => void;
  resetAll: () => void;
}

const AdminContext =
  createContext<AdminContextType | null>(null);

export function AdminProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [data, setData] =
    useState<AdminData>(initialData);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [loaded, setLoaded] =
    useState(false);

  const saveTimerRef =
    useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

  const saveQueueRef =
    useRef<Promise<void>>(
      Promise.resolve(),
    );

  const lastServerSnapshotRef =
    useRef("");

  const applyServerData = useCallback(
    (serverData: AdminData) => {
      setData(serverData);

      lastServerSnapshotRef.current =
        JSON.stringify(serverData);

      try {
        localStorage.removeItem(
          LEGACY_STORAGE_KEY,
        );
      } catch {
        /* ignora */
      }
    },
    [],
  );

  const refreshData =
    useCallback(async () => {
      setLoading(true);
      setError("");

      try {
        const serverData =
          await loadDataFromServer();

        applyServerData(serverData);

        setLoaded(true);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os dados do site.";

        setError(message);
      } finally {
        setLoading(false);
      }
    }, [applyServerData]);

  useEffect(() => {
    let active = true;

    const loadInitialData = async () => {
      setLoading(true);
      setError("");

      try {
        const serverData =
          await loadDataFromServer();

        if (!active) {
          return;
        }

        applyServerData(serverData);

        setLoaded(true);
      } catch (err) {
        if (!active) {
          return;
        }

        const message =
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os dados do site.";

        setError(message);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadInitialData();

    return () => {
      active = false;

      if (saveTimerRef.current) {
        clearTimeout(
          saveTimerRef.current,
        );
      }
    };
  }, [applyServerData]);

  /*
  |--------------------------------------------------------------------------
  | SALVAMENTO AUTOMÁTICO NO BANCO
  |--------------------------------------------------------------------------
  |
  | O painel já altera o AdminContext diretamente.
  | Para não modificar todo o layout e todos os formulários do Dashboard,
  | mantemos esse comportamento e salvamos a versão mais recente no banco.
  |
  | O pequeno atraso evita uma requisição para cada tecla digitada.
  |
  */

  useEffect(() => {
    if (!loaded) {
      return;
    }

    const currentSnapshot =
      JSON.stringify(data);

    if (
      currentSnapshot ===
      lastServerSnapshotRef.current
    ) {
      return;
    }

    if (saveTimerRef.current) {
      clearTimeout(
        saveTimerRef.current,
      );
    }

    saveTimerRef.current =
      setTimeout(() => {
        const snapshotData =
          JSON.parse(
            currentSnapshot,
          ) as AdminData;

        saveQueueRef.current =
          saveQueueRef.current
            .catch(() => {
              /* mantém a fila ativa */
            })
            .then(async () => {
              setSaving(true);
              setError("");

              try {
                const savedData =
                  await saveDataToServer(
                    snapshotData,
                  );

                lastServerSnapshotRef.current =
                  JSON.stringify(
                    savedData,
                  );
              } catch (err) {
                const message =
                  err instanceof Error
                    ? err.message
                    : "Não foi possível salvar as alterações.";

                setError(message);

                console.error(
                  "Erro ao salvar dados:",
                  err,
                );
              } finally {
                setSaving(false);
              }
            });
      }, 650);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(
          saveTimerRef.current,
        );
      }
    };
  }, [data, loaded]);

  const updateSettings =
    useCallback(
      (
        settings: Partial<SiteSettings>,
      ) => {
        setData((prev) => ({
          ...prev,
          settings: {
            ...prev.settings,
            ...settings,
          },
        }));
      },
      [],
    );

  const setBanners =
    useCallback(
      (banners: Banner[]) => {
        setData((prev) => ({
          ...prev,
          banners,
        }));
      },
      [],
    );

  const setBrands =
    useCallback(
      (brands: Brand[]) => {
        setData((prev) => ({
          ...prev,
          brands,
        }));
      },
      [],
    );

  const setCategories =
    useCallback(
      (categories: Category[]) => {
        setData((prev) => ({
          ...prev,
          categories,
        }));
      },
      [],
    );

  const setProducts =
    useCallback(
      (products: Product[]) => {
        setData((prev) => ({
          ...prev,
          products: products.map(
            normalizeProduct,
          ),
        }));
      },
      [],
    );

  const setSellers =
    useCallback(
      (sellers: Seller[]) => {
        setData((prev) => ({
          ...prev,
          sellers: sellers.map(
            normalizeSeller,
          ),
        }));
      },
      [],
    );

  const setNews =
    useCallback(
      (news: NewsItem[]) => {
        setData((prev) => ({
          ...prev,
          news,
        }));
      },
      [],
    );

  const setRssSources =
    useCallback(
      (rssSources: RssSource[]) => {
        setData((prev) => ({
          ...prev,
          rssSources,
        }));
      },
      [],
    );

  const setStores =
    useCallback(
      (stores: Store[]) => {
        setData((prev) => ({
          ...prev,
          stores,
        }));
      },
      [],
    );

  const setCompanyInfo =
    useCallback(
      (
        companyInfo: CompanyInfo,
      ) => {
        setData((prev) => ({
          ...prev,
          companyInfo,
        }));
      },
      [],
    );

  const setCompanyTimeline =
    useCallback(
      (
        companyTimeline: CompanyTimelineItem[],
      ) => {
        setData((prev) => ({
          ...prev,
          companyTimeline:
            companyTimeline.map(
              normalizeCompanyTimelineItem,
            ),
        }));
      },
      [],
    );

  const setOutletPage =
    useCallback(
      (outletPage: OutletPage) => {
        setData((prev) => ({
          ...prev,
          outletPage: normalizeOutletPage(outletPage),
        }));
      },
      [],
    );

  const setOutletProducts =
    useCallback(
      (outletProducts: number[]) => {
        setData((prev) => ({
          ...prev,
          outletProducts: outletProducts
            .map(Number)
            .filter((id, index, list) => id > 0 && list.indexOf(id) === index),
        }));
      },
      [],
    );

  const setPromotions =
    useCallback(
      (promotions: number[]) => {
        setData((prev) => ({
          ...prev,
          promotions,
        }));
      },
      [],
    );

  const setTestimonials =
    useCallback(
      (
        testimonials: Testimonial[],
      ) => {
        setData((prev) => ({
          ...prev,
          testimonials: testimonials.map(
            normalizeTestimonial,
          ),
        }));
      },
      [],
    );

  const setJobVacancies =
    useCallback(
      (vacancies: JobVacancy[]) => {
        setData((prev) => ({
          ...prev,
          jobVacancies: vacancies.map(
            normalizeJobVacancy,
          ),
        }));
      },
      [],
    );

  const setRaffles =
    useCallback(
      (raffles: Raffle[]) => {
        setData((prev) => ({
          ...prev,
          raffles: raffles.map(
            normalizeRaffle,
          ),
        }));
      },
      [],
    );

  /*
  |--------------------------------------------------------------------------
  | RESET
  |--------------------------------------------------------------------------
  |
  | Os mocks não existem mais como fonte de dados.
  | Por segurança, este método agora apenas descarta alterações locais ainda
  | não sincronizadas e recarrega a versão oficial existente no banco.
  |
  */

  const resetAll =
    useCallback(() => {
      void refreshData();
    }, [refreshData]);

  /*
  |--------------------------------------------------------------------------
  | CARREGAMENTO INICIAL
  |--------------------------------------------------------------------------
  */

  if (loading && !loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 block mb-3" />

          <p className="text-sm font-label text-foreground-600">
            Carregando dados...
          </p>
        </div>
      </div>
    );
  }

  if (error && !loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="max-w-md text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 block mb-3" />

          <h2 className="font-heading font-bold text-foreground-950 text-xl mb-2">
            Não foi possível carregar o site
          </h2>

          <p className="text-sm text-foreground-600 mb-5">
            {error}
          </p>

          <button
            type="button"
            onClick={() => {
              void refreshData();
            }}
            className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-md text-sm font-label font-semibold cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminContext.Provider
      value={{
        data,
        loading,
        saving,
        error,
        refreshData,
        updateSettings,
        setBanners,
        setBrands,
        setCategories,
        setProducts,
        setSellers,
        setNews,
        setRssSources,
        setStores,
        setCompanyInfo,
        setCompanyTimeline,
        setOutletPage,
        setOutletProducts,
        setPromotions,
        setTestimonials,
        setJobVacancies,
        setRaffles,
        resetAll,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin(): AdminContextType {
  const context =
    useContext(AdminContext);

  if (!context) {
    throw new Error(
      "useAdmin must be used within AdminProvider",
    );
  }

  return context;
}